import type { ApiError, GuestSession, GuestSessionResponse } from '~/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/** Public for callers that need the same base (e.g. claim-handoff window navigation). */
export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

/**
 * Global error listener for API failures.
 * Used by analytics to track client-side errors without coupling
 * the API client to analytics directly.
 */
type ApiErrorListener = (endpoint: string, statusCode: number, message: string) => void;
const errorListeners: ApiErrorListener[] = [];

export function onApiError(listener: ApiErrorListener): () => void {
  errorListeners.push(listener);
  return () => {
    const index = errorListeners.indexOf(listener);
    if (index !== -1) errorListeners.splice(index, 1);
  };
}

function notifyErrorListeners(endpoint: string, statusCode: number, message: string): void {
  for (const listener of errorListeners) {
    try {
      listener(endpoint, statusCode, message);
    } catch {
      // Error listeners should never break API client
    }
  }
}

/**
 * Custom error class for API errors with status code and structured message.
 */
export class ApiRequestError extends Error {
  public statusCode: number;
  public errorBody: ApiError | null;

  constructor(statusCode: number, message: string, errorBody: ApiError | null = null) {
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
    this.errorBody = errorBody;
  }
}

// =============================================================================
// Session-expiry signaling
// =============================================================================

/**
 * Sentinel thrown by the guest-auth wrapper after a 401 → renewal_rejected
 * (or repeat-401 after a successful renewal). Callers SHOULD let this bubble;
 * the global expiry handler navigates the user back to the landing page.
 */
export class GuestSessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Please play again.') {
    super(message);
    this.name = 'GuestSessionExpiredError';
  }
}

type ExpiryListener = () => void;
const expiryListeners: ExpiryListener[] = [];

/**
 * Subscribe to guest-session expiry. Fires once the guest-auth wrapper has
 * cleared local session state and is ready for a visible restart.
 */
export function onGuestSessionExpired(listener: ExpiryListener): () => void {
  expiryListeners.push(listener);
  return () => {
    const index = expiryListeners.indexOf(listener);
    if (index !== -1) expiryListeners.splice(index, 1);
  };
}

function notifyExpiryListeners(): void {
  for (const listener of expiryListeners) {
    try {
      listener();
    } catch {
      // Listeners must never break the API client.
    }
  }
}

// =============================================================================
// API Client
// =============================================================================

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

interface GuestAuthFetchOptions extends FetchOptions {
  /**
   * Active guest session JWT + identifiers. Must be present for guest-auth
   * calls — pass the value returned by `useGuestToken`.
   */
  guestSession: GuestSession;
  /** Callback invoked when the wrapper successfully renews the JWT. */
  onSessionRenewed?: (next: GuestSession) => void;
}

/**
 * API client fetch wrapper for the public play funnel.
 * - Configurable base URL via VITE_API_BASE_URL
 * - JSON request/response handling
 * - Structured error handling with ApiRequestError
 */
export async function apiClient<T>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<T> {
  const { body, headers: customHeaders, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...((customHeaders as Record<string, string>) || {}),
  };

  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let errorBody: ApiError | null = null;
    try {
      errorBody = await response.json() as ApiError;
    } catch {
      // Response body may not be JSON
    }

    const errorMessage = errorBody?.message || `API request failed with status ${response.status}`;
    notifyErrorListeners(endpoint, response.status, errorMessage);

    throw new ApiRequestError(
      response.status,
      errorMessage,
      errorBody,
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Guest-authenticated API client.
 *
 * Threads the guest JWT into `Authorization: Bearer <token>`. If the upstream
 * call returns 401, attempts ONE renewal cycle:
 *   1. POST `/v1/public/guest-sessions/renew` with the expired JWT.
 *   2. On success: persist the new session, retry the original call once.
 *   3. On `renewal_rejected` (or any 401): clear local session, fire the
 *      expiry signal, and throw `GuestSessionExpiredError`.
 *
 * NEVER silently re-issues a new guest session — that path is reserved for
 * the visible-restart UX surfaced by `onGuestSessionExpired`.
 */
export async function apiClientWithGuestAuth<T>(
  endpoint: string,
  options: GuestAuthFetchOptions,
): Promise<T> {
  const { guestSession, onSessionRenewed, headers: customHeaders, ...rest } = options;

  const authHeaders = {
    ...(customHeaders as Record<string, string> | undefined),
    Authorization: `Bearer ${guestSession.token}`,
  };

  try {
    return await apiClient<T>(endpoint, { ...rest, headers: authHeaders });
  } catch (err) {
    if (!(err instanceof ApiRequestError) || err.statusCode !== 401) {
      throw err;
    }

    // 401 — attempt one renewal with the (now-expired) JWT.
    let renewed: GuestSession;
    try {
      const renewedResponse = await apiClient<GuestSessionResponse>(
        '/v1/public/guest-sessions/renew',
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${guestSession.token}` },
        },
      );
      renewed = {
        ...renewedResponse,
        sessionRef: renewedResponse.guestSessionId,
      };
    } catch {
      // Renewal rejected (or any other failure) — visible restart.
      notifyExpiryListeners();
      throw new GuestSessionExpiredError();
    }

    onSessionRenewed?.(renewed);

    // Retry the original call ONCE with the fresh token. A repeat 401 falls
    // through to the visible restart — no second renewal attempt.
    try {
      return await apiClient<T>(endpoint, {
        ...rest,
        headers: { ...authHeaders, Authorization: `Bearer ${renewed.token}` },
      });
    } catch (retryErr) {
      if (retryErr instanceof ApiRequestError && retryErr.statusCode === 401) {
        notifyExpiryListeners();
        throw new GuestSessionExpiredError();
      }
      throw retryErr;
    }
  }
}
